/**
 * Undo / redo for the forecast grid's unsaved edits (1st Phorm, 10/29).
 *
 * The grid's state is an immutable tree (every edit returns a new root and shares what it did not
 * touch), so a history step is just the previous root: cheap to keep, exact to restore. Loading a
 * version starts a fresh history; a new edit after an undo drops the redo branch, as editors do.
 */

export interface UndoHistory<T> {
  past: T[]
  future: T[]
}

/** How many steps back the grid keeps. */
export const UNDO_LIMIT = 50

export function empty_history<T>(): UndoHistory<T> {
  return { past: [], future: [] }
}

/** A new edit replaced `previous`: it becomes the last undo step and the redo branch is gone. */
export function record_step<T>(history: UndoHistory<T>, previous: T, limit: number = UNDO_LIMIT): UndoHistory<T> {
  const past = [...history.past, previous]
  return { past: past.length > limit ? past.slice(past.length - limit) : past, future: [] }
}

/** Step back from `current`, or `null` when there is nothing to undo. */
export function undo_step<T>(history: UndoHistory<T>, current: T): { history: UndoHistory<T>; value: T } | null {
  if (history.past.length === 0) return null
  const value = history.past[history.past.length - 1]
  return { history: { past: history.past.slice(0, -1), future: [current, ...history.future] }, value }
}

/** Step forward from `current`, or `null` when there is nothing to redo. */
export function redo_step<T>(history: UndoHistory<T>, current: T): { history: UndoHistory<T>; value: T } | null {
  if (history.future.length === 0) return null
  const [value, ...future] = history.future
  return { history: { past: [...history.past, current], future }, value }
}

/** Which history action a key press asks for: ⌘/Ctrl+Z undo, ⇧⌘/Ctrl+Z or Ctrl+Y redo. */
export function undo_key_action(event: { key: string; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean }): "undo" | "redo" | null {
  const mod = event.metaKey || event.ctrlKey
  if (!mod) return null
  const key = event.key.toLowerCase()
  if (key === "z") return event.shiftKey ? "redo" : "undo"
  if (key === "y" && event.ctrlKey && !event.metaKey) return "redo"
  return null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("undo history", () => {
    it("undoes and redoes edits in order", () => {
      let history = empty_history<string>()
      history = record_step(history, "a") // a → b
      history = record_step(history, "b") // b → c
      const back = undo_step(history, "c")!
      expect(back.value).toBe("b")
      const back2 = undo_step(back.history, back.value)!
      expect(back2.value).toBe("a")
      expect(undo_step(back2.history, back2.value)).toBeNull()
      const forward = redo_step(back2.history, back2.value)!
      expect(forward.value).toBe("b")
      const forward2 = redo_step(forward.history, forward.value)!
      expect(forward2.value).toBe("c")
      expect(redo_step(forward2.history, forward2.value)).toBeNull()
    })

    it("drops the redo branch on a new edit and keeps only the last steps", () => {
      let history = record_step(record_step(empty_history<number>(), 1), 2)
      const back = undo_step(history, 3)!
      expect(back.history.future).toEqual([3])
      history = record_step(back.history, back.value)
      expect(history.future).toEqual([])
      let long = empty_history<number>()
      for (let i = 0; i < 60; i++) long = record_step(long, i, 50)
      expect(long.past.length).toBe(50)
      expect(long.past[0]).toBe(10)
    })

    it("reads the usual shortcuts", () => {
      expect(undo_key_action({ key: "z", metaKey: true, ctrlKey: false, shiftKey: false })).toBe("undo")
      expect(undo_key_action({ key: "Z", metaKey: true, ctrlKey: false, shiftKey: true })).toBe("redo")
      expect(undo_key_action({ key: "y", metaKey: false, ctrlKey: true, shiftKey: false })).toBe("redo")
      expect(undo_key_action({ key: "z", metaKey: false, ctrlKey: false, shiftKey: false })).toBeNull()
    })
  })
}
